import { useEffect, useRef, useState } from "react";

function parseTarget(text) {
  // Skip animation for tokens like "24/7" where the slash is meaningful
  // and any non-strict-number format. We only animate clean numeric values.
  const raw = String(text);
  if (raw.includes("/")) {
    return { value: 0, prefix: "", suffix: raw, hasDecimal: false, raw, animatable: false };
  }
  const cleaned = raw.replace(/,/g, "");
  const m = cleaned.match(/^([^\d.-]*)(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return { value: 0, prefix: "", suffix: raw, hasDecimal: false, raw, animatable: false };
  return {
    value: parseFloat(m[2]),
    prefix: m[1] || "",
    suffix: m[3] || "",
    hasDecimal: m[2].includes("."),
    raw,
    animatable: true,
  };
}

/**
 * Animated count-up counter. Triggers when scrolled into view (IntersectionObserver).
 * Pass the display string e.g. "$340M+", "12,400+", "4.9★", "24/7".
 */
export default function AnimatedCounter({
  value,
  durationMs = 1400,
  className = "",
}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const parsed = parseTarget(value);
  const { value: target, prefix, suffix, hasDecimal, raw, animatable } = parsed;

  useEffect(() => {
    if (!ref.current || !animatable) return;
    let started = false;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / durationMs);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(target * eased);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(target);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, durationMs, animatable]);

  if (!animatable) {
    return (
      <span ref={ref} className={className} data-testid="animated-counter">
        {raw}
      </span>
    );
  }

  const decimals = hasDecimal ? 1 : 0;
  const formatted = display.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}
